import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import Undo from '@mui/icons-material/Undo'

export default function RevertEventsDialog({
  open,
  onClose,
  onRevert,
  currentVersion,
  isLoading = false,
}) {
  const [note, setNote] = useState('')
  const [targetVersion, setTargetVersion] = useState('')

  const handleClose = () => {
    setNote('')
    setTargetVersion('')
    onClose()
  }

  const handleRevert = () => {
    const versionToRevert = targetVersion.trim() 
      ? parseInt(targetVersion.trim(), 10) 
      : null
    
    // Validate version if provided
    if (targetVersion.trim() && (isNaN(versionToRevert) || versionToRevert < 1)) {
      return // Could show error here
    }

    onRevert(versionToRevert, note.trim())
    handleClose()
  }

  const previousVersion = currentVersion !== null && currentVersion > 1 
    ? currentVersion - 1 
    : null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="revert-events-dialog-title"
    >
      <DialogTitle id="revert-events-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Undo sx={{ color: 'warning.main' }} />
          <Typography variant="h6" component="span">
            Revert Event Configuration
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {currentVersion !== null && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Version
              </Typography>
              <Typography variant="h6" color="primary">
                Version {currentVersion}
              </Typography>
            </Box>
          )}

          <TextField
            label="Target Version (Optional)"
            helperText={
              previousVersion
                ? `Leave empty to revert to previous version (${previousVersion})`
                : 'Leave empty to revert to previous version, or specify a version number'
            }
            type="number"
            value={targetVersion}
            onChange={(e) => setTargetVersion(e.target.value)}
            fullWidth
            variant="outlined"
            disabled={isLoading}
            inputProps={{ min: 1 }}
          />

          <TextField
            label="Reason for Revert"
            helperText="Please provide a reason for reverting this configuration (required for audit)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            disabled={isLoading}
            required
          />

          <Box
            sx={{
              p: 2,
              bgcolor: 'warning.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main',
            }}
          >
            <Typography variant="body2" color="warning.dark">
              <strong>Warning:</strong> This will restore the event configuration to a previous
              version. Any changes made after that version will be lost. This action cannot be
              undone, but a new version will be created for audit purposes.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleRevert}
          variant="contained"
          color="warning"
          disabled={isLoading || !note.trim()}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Undo />}
        >
          {isLoading ? 'Reverting...' : 'Revert Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}






